import {get} from 'lodash';
import React from 'react';
import {useProfile} from 'hooks/datahook/auth';

/**
 * Resolves whether to render the new design system for the current user.
 * Priority (top wins):
 *   1. localStorage 'nx-force' === 'new'  → true  (dev/QA only)
 *   2. localStorage 'nx-force' === 'old'  → false (dev/QA only)
 *   3. profile.preference.newUi (admin SecurityPanel toggle, default false)
 *
 * The localStorage override only fires in non-production builds so that QA
 * can flip a single account without round-tripping to the database.
 */
const resolveNewUi = (profile) => {
    if (process.env.NODE_ENV !== 'production') {
        try {
            const forced = window.localStorage.getItem('nx-force');
            if (forced === 'new') return true;
            if (forced === 'old') return false;
        } catch (e) {
            // localStorage may be unavailable (private mode, blocked) — fall through
        }
    }
    return Boolean(get(profile, 'preference.newUi'));
};

/**
 * Dual-track switch. The old and new implementations are passed in as already-
 * lazy components so the unused chunk is never pulled in. When newUi is true the
 * chosen component is wrapped in <div className="nx"> so all new-UI selectors
 * match; nesting .nx inside .nx is safe (token values are identical).
 *
 *  <Versioned old={Dashboard} next={DashboardNext} />
 */
const Versioned = ({old: Old, next: Next, ...rest}) => {
    const {data: profile} = useProfile();
    const newUi = resolveNewUi(profile);
    const Chosen = newUi ? Next : Old;
    if (!Chosen) return null;
    return newUi ? (
        <div className="nx">
            <Chosen {...rest} />
        </div>
    ) : (
        <Chosen {...rest} />
    );
};

export default Versioned;
export {resolveNewUi};
